import { resolveHexPathBrushMask } from './hexPathBrushCatalog';
import { resolveSquarePathBrushShape } from './squarePathProcedural';
import { resolveTrianglePathBrush } from './trianglePathProcedural';

export type FixedPathVariantProfile = 'square' | 'hex' | 'triangle';

export interface FixedPathVariant {
  readonly profile: FixedPathVariantProfile;
  readonly brushIndex: number;
  readonly variantId: string;
}

function normalizeGridProfile(gridType: string): FixedPathVariantProfile | null {
  if (gridType === 'square' || gridType === 'hex' || gridType === 'triangle') {
    return gridType;
  }
  return null;
}

function isValidBrushIndex(profile: FixedPathVariantProfile, brushIndex: number): boolean {
  if (!Number.isFinite(brushIndex) || Math.trunc(brushIndex) !== brushIndex || brushIndex < 0) {
    return false;
  }
  if (profile === 'square') {
    return resolveSquarePathBrushShape(brushIndex) !== null;
  }
  if (profile === 'hex') {
    return resolveHexPathBrushMask(brushIndex) !== null;
  }
  return resolveTrianglePathBrush(brushIndex) !== null;
}

export function encodeFixedPathVariantId(gridType: string, brushIndex: number): string | null {
  const profile = normalizeGridProfile(gridType);
  if (!profile || !isValidBrushIndex(profile, brushIndex)) {
    return null;
  }
  return `${profile}-brush-${brushIndex}`;
}

export function decodeFixedPathVariantId(
  gridType: string,
  variantId: string,
): FixedPathVariant | null {
  const profile = normalizeGridProfile(gridType);
  if (!profile || typeof variantId !== 'string') {
    return null;
  }
  const match = /^(square|hex|triangle)-brush-(\d+)$/.exec(variantId.trim());
  if (!match || match[1] !== profile) {
    return null;
  }
  const brushIndex = Number.parseInt(match[2] ?? '', 10);
  if (!isValidBrushIndex(profile, brushIndex)) {
    return null;
  }
  return {
    profile,
    brushIndex,
    variantId: `${profile}-brush-${brushIndex}`,
  };
}

export function isFixedPathVariantId(gridType: string, variantId: string): boolean {
  return decodeFixedPathVariantId(gridType, variantId) !== null;
}
